import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, ChevronLeft, ChevronRight, Edit, Trash2,
  Battery, Zap, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const initialFormData = {
  modelo: '',
  fabricante_id: '',
  potencia_va: '',
  description: '',
};

const ModelosUPSPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'modelos_ups');
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, by_fabricante: [], total_va: 0 });
  const [formOptions, setFormOptions] = useState({ fabricantes: [] });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'modelo', dir: 'asc' });
  
  // Filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    fabricante_id: '',
  });
  
  // Modals
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/config/modelos-ups/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/config/modelos-ups/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchModelos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);
      if (columnFilters.fabricante_id && columnFilters.fabricante_id !== 'all') {
        params.append('fabricante_id', columnFilters.fabricante_id);
      }

      const response = await api.get(`/config/modelos-ups?${params}`);
      setModelos(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar modelos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFormOptions();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchModelos();
  }, [pagination.page, sortConfig]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchModelos();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD handlers
  const handleCreate = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, item: null });
  };

  const handleEdit = (item) => {
    setFormData({
      modelo: item.modelo || '',
      fabricante_id: item.fabricante_id ? String(item.fabricante_id) : '',
      potencia_va: item.potencia_va ? String(item.potencia_va) : '',
      description: item.description || '',
    });
    setEditModal({ open: true, item });
  };

  const handleSave = async () => {
    if (!formData.modelo.trim()) {
      toast.error('El nombre del modelo es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        modelo: formData.modelo.trim(),
        fabricante_id: formData.fabricante_id ? parseInt(formData.fabricante_id) : null,
        potencia_va: formData.potencia_va ? parseInt(formData.potencia_va) : null,
        description: formData.description || null,
      };
      
      if (editModal.item) {
        await api.put(`/config/modelos-ups/${editModal.item.id}`, payload);
        toast.success('Modelo actualizado');
      } else {
        await api.post('/config/modelos-ups', payload);
        toast.success('Modelo creado');
      }
      setEditModal({ open: false, item: null });
      fetchModelos();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/config/modelos-ups/${deleteModal.item.id}`);
      toast.success('Modelo eliminado');
      setDeleteModal({ open: false, item: null });
      fetchModelos();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Format VA
  const formatVA = (va) => {
    if (!va) return '-';
    return va >= 1000 ? `${(va / 1000).toFixed(1)} kVA` : `${va} VA`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Modelos de UPS</h1>
            <p className="page-subtitle">Gestión del catálogo de modelos de UPS</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-modelo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Modelo
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="modelos-ups-stats">
          <div className="stats-card" data-testid="stats-total">
            <div className="mb-2">
              <Battery className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total Modelos</div>
          </div>
          
          <div className="stats-card" data-testid="stats-total-va">
            <div className="mb-2">
              <Zap className="h-8 w-8 text-yellow-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-yellow-500">{formatVA(stats.total_va)}</div>
            <div className="stats-card-label">Potencia Total</div>
          </div>
          
          <div className="stats-card" data-testid="stats-fabricantes">
            <div className="mb-2">
              <Battery className="h-8 w-8 text-blue-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-blue-500">{stats.by_fabricante?.length || 0}</div>
            <div className="stats-card-label">Fabricantes</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('modelo')}
                        >
                          <div className="flex items-center gap-1">
                            MODELO
                            <SortIcon field="modelo" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('fabricante')}
                        >
                          <div className="flex items-center gap-1">
                            FABRICANTE
                            <SortIcon field="fabricante" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none text-center"
                          onClick={() => handleSort('potencia_va')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            POTENCIA
                            <SortIcon field="potencia_va" />
                          </div>
                        </th>
                        <th>DESCRIPCIÓN</th>
                        <th className="w-20 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar modelo..."
                            value={columnFilters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select 
                            value={columnFilters.fabricante_id || "all"} 
                            onValueChange={(v) => handleFilterChange('fabricante_id', v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {formOptions.fabricantes?.map(f => (
                                <SelectItem key={f.id} value={String(f.id)}>{f.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modelos.length === 0 ? (
                        <tr>
                          <td colSpan={5}>
                            <div className="empty-state">
                              <Battery className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron modelos</p>
                            </div>
                          </td>
                        </tr>
                      ) : modelos.map((modelo) => (
                        <tr key={modelo.id} data-testid={`modelo-row-${modelo.id}`}>
                          <td className="font-medium text-primary">{modelo.modelo}</td>
                          <td>
                            {modelo.fabricante_nombre ? (
                              <span className="badge badge-info">{modelo.fabricante_nombre}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {modelo.potencia_va ? (
                              <span className="badge badge-warning">{formatVA(modelo.potencia_va)}</span>
                            ) : '-'}
                          </td>
                          <td className="text-muted-foreground text-sm max-w-[200px] truncate">
                            {modelo.description || '-'}
                          </td>
                          <td className="w-20">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => canUpdate && handleEdit(modelo)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-modelo-${modelo.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, item: modelo })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-modelo-${modelo.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {pagination.total > 0 ? `${(pagination.page - 1) * pagination.page_size + 1} - ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{pagination.page} / {pagination.pages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                      disabled={pagination.page >= pagination.pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit/Create Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, item: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Battery className="h-5 w-5 text-primary" />
                {editModal.item ? 'Editar Modelo' : 'Nuevo Modelo'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Nombre del Modelo *</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Ej: 3000 VA SRV3KI-AR"
                  data-testid="input-modelo"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fabricante</Label>
                <Select
                  value={formData.fabricante_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, fabricante_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-fabricante">
                    <SelectValue placeholder="Seleccionar fabricante" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin fabricante</SelectItem>
                    {formOptions.fabricantes?.map(f => (
                      <SelectItem key={f.id} value={String(f.id)}>{f.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Potencia (VA)</Label>
                <Input
                  type="number"
                  value={formData.potencia_va}
                  onChange={(e) => setFormData(prev => ({ ...prev, potencia_va: e.target.value }))}
                  placeholder="Ej: 3000"
                  data-testid="input-potencia"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción opcional"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, item: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Modelo
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estás seguro que deseas eliminar el modelo <strong>{deleteModal.item?.modelo}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ModelosUPSPage;
